<template>
  <div class="card-member-place" v-on:click="$_onClickCard">
    <div class="header">
      <!-- プレイス名 -->
      <div class="title-row">
        <p class="title" v-on:click.stop="$_onClickTitle">
          {{ placeNameText }}
        </p>
        <span v-if="existsAttachFiles" class="attach-file">
          {{ attachFilesCount }}
        </span>
        <span
          v-if="!isFixedPosition"
          v-t="'appCardMemberPlace.positionUnfixed'"
          class="position-unfixed"
        />
      </div>
      <!-- グループ -->
      <div class="field">
        <span v-t="'appCardMemberPlace.field.group'" class="field-label" />
        <span class="field-value ellipsis">{{ groupText }}</span>
      </div>
      <!-- 担当者 -->
      <div class="field">
        <span
          v-t="'appCardMemberPlace.field.relatedUser'"
          class="field-label"
        />
        <template v-if="otherRelationNames.length">
          <span class="field-value ellipsis trailing">
            {{ firstRelationName }}
          </span>
          <span class="field-value separator">,</span>
          <span class="field-value others" v-b-tooltip="tooltipOptionOthers">
            {{ otherRelationsText }}
          </span>
        </template>
        <span v-else class="field-value ellipsis">
          {{ firstRelationName }}
        </span>
      </div>
      <!-- 住所 -->
      <div class="field">
        <span v-t="'appCardMemberPlace.field.address'" class="field-label" />
        <span class="field-value ellipsis">{{ addressText }}</span>
      </div>
    </div>
    <!-- 任意項目 -->
    <div
      v-if="!simple && itemsModified.length"
      class="items-container"
      v-bind:style="{ '--background-color': itemBackgroundColor }"
    >
      <component
        v-for="item in itemsModified"
        v-bind:key="item.itemType"
        v-bind:is="item.component"
        v-bind="item"
      />
    </div>
  </div>
</template>

<script>
import {
  CardMemberItemIndoor,
  CardMemberItemSensorData,
  CardMemberItemUserAttribute,
} from '@/components/member/carditem'
import {
  PLACE_ITEM_TYPE,
  getPlaceItemDefaultValue,
  withPlaceItemUnit,
} from '@/js/util/PlaceLabelUtil.js'
import StringUtil from '@/js/util/StringUtil.js'

/**
 * タイムラインタイプ（機能のタイプ）
 *
 * プレイス画面のカードと同じ値を渡す。呼び出し元の受け口を変えないため
 * @type {Number}
 */
const TIMELINE_TYPE = 8

/**
 * 値が無いときに出す文字
 * @type {String}
 */
const EMPTY_TEXT = '-'

/**
 * プレイス名を省略せずに出せる上限の文字数
 *
 * これ以上のときは上限文字目を「…」に置き換える
 * @type {Number}
 */
const PLACE_NAME_MAX_LENGTH = 18

/**
 * 表示項目の種別と描画コンポーネントの対応
 *
 * 屋内・プレイス属性・センサー値はユーザーの任意項目とレスポンスのスキーマが同じなので
 * ユーザー側のコンポーネントをそのまま使う。プレイス固有の4種も「値 (項目名)」の形が
 * プレイス属性と同じなので、属性のコンポーネントに項目名を渡して描く
 * @type {Map.<String, Object>}
 */
const TYPE_COMPONENT_MAP = new Map([
  [PLACE_ITEM_TYPE.PLACE_CODE, CardMemberItemUserAttribute],
  [PLACE_ITEM_TYPE.CHECKIN_RADIUS, CardMemberItemUserAttribute],
  [PLACE_ITEM_TYPE.TEL, CardMemberItemUserAttribute],
  [PLACE_ITEM_TYPE.FAX, CardMemberItemUserAttribute],
  [PLACE_ITEM_TYPE.INDOOR, CardMemberItemIndoor],
  [PLACE_ITEM_TYPE.ATTRIBUTE, CardMemberItemUserAttribute],
  [PLACE_ITEM_TYPE.SENSOR_DATA, CardMemberItemSensorData],
  // 値と項目名を並べるだけなので、ユーザーカードの紐付けセンサー名と同じ部品を使う
  [PLACE_ITEM_TYPE.SENSOR_NAMES, CardMemberItemUserAttribute],
])

/**
 * 値が「種別と同じ名前のフィールド」に入る項目
 *
 * 例: `{ item_type: 'place_code', place_code: '1234' }`
 * @type {Array.<String>}
 */
const VALUE_FIELD_ITEM_TYPES = [
  PLACE_ITEM_TYPE.PLACE_CODE,
  PLACE_ITEM_TYPE.CHECKIN_RADIUS,
  PLACE_ITEM_TYPE.TEL,
  PLACE_ITEM_TYPE.FAX,
]

/**
 * 地図画面のプレイスタブで使うプレイスカード
 *
 * ヘッダー（プレイス名・グループ・担当者・住所）は常に固定で出し、その下に
 * システム設定「プレイス表示項目」で選ばれた任意項目を出す。IFIELD_V4-7150。
 *
 * プレイス画面（`ThePlace.vue`）の `AppCardPlace.vue` とは別物として作ってある。
 * デザインが変わったのは地図画面のプレイスタブだけで、共通化すると
 * プレイス画面まで見た目が変わってしまうため
 */
export default {
  name: 'AppCardMemberPlace',
  props: {
    /**
     * プレイスID
     */
    id: {
      type: Number,
      default: null,
      required: true,
    },
    /**
     * プレイス名
     */
    placeName: {
      type: String,
      default: null,
    },
    /**
     * プレイス住所
     */
    placeAddress: {
      type: String,
      default: null,
    },
    /**
     * 添付ファイル数
     */
    attachFilesCount: {
      type: Number,
      default: null,
    },
    /**
     * プレイスグループ名リスト
     */
    placeGroupNames: {
      type: Array,
      default: () => [],
    },
    /**
     * 担当者名リスト
     */
    placeUserRelationNames: {
      type: Array,
      default: () => [],
    },
    /**
     * 位置確定している場合はtrue
     */
    isFixedPosition: {
      type: Boolean,
      default: false,
    },
    /**
     * 屋内プレイスの場合はフロアID
     *
     * 自動チェックイン半径の既定値が屋内と屋外で違うので受け取る
     */
    indoorLevelId: {
      type: Number,
      default: null,
    },
    /**
     * 簡易表示の場合はtrue
     */
    simple: {
      type: Boolean,
      default: false,
    },
    /**
     * 任意項目（プレイス一覧APIの `additional_items`）
     */
    items: {
      type: Array,
      default: () => [],
    },
  },
  data: function () {
    return {
      // アイテム部の背景色
      itemBackgroundColor: '#f6f6f6',
      // 1remのサイズ(px)
      remSize: 16,
    }
  },
  computed: {
    /**
     * プレイス名の表示テキスト
     *
     * 上限を超える場合は上限文字目を「…」に置き換える
     * @type {String}
     */
    placeNameText: function () {
      const name = this.placeName || ''
      if (name.length < PLACE_NAME_MAX_LENGTH) return name
      return `${name.slice(0, PLACE_NAME_MAX_LENGTH - 1)}…`
    },
    /**
     * 添付ファイルが存在する場合はtrue
     * @type {Boolean}
     */
    existsAttachFiles: function () {
      return this.attachFilesCount > 0
    },
    /**
     * グループの表示テキスト
     * @type {String}
     */
    groupText: function () {
      return this.placeGroupNames?.length
        ? this.placeGroupNames.join(', ')
        : EMPTY_TEXT
    },
    /**
     * 先頭に出す担当者の名称
     * @type {String}
     */
    firstRelationName: function () {
      return this.placeUserRelationNames?.length
        ? this.placeUserRelationNames[0]
        : EMPTY_TEXT
    },
    /**
     * 先頭に出さない担当者の名称
     * @type {Array.<String>}
     */
    otherRelationNames: function () {
      return (this.placeUserRelationNames || []).slice(1)
    },
    /**
     * 住所の表示テキスト
     * @type {String}
     */
    addressText: function () {
      return this.placeAddress || EMPTY_TEXT
    },
    /**
     * 「他N名」のツールチップオプション
     * @type {Object}
     */
    tooltipOptionOthers: function () {
      return {
        title: this.otherRelationNames.join(', '),
        placement: 'right',
        boundary: 'window',
        container: 'body',
        customClass: 'ifield-tooltip-light',
      }
    },
    /**
     * 表示用に加工した任意項目
     * @type {Array.<Object>}
     */
    itemsModified: function () {
      return (this.items || []).map((item) => {
        const type = `${item.item_type ?? ''}`.split(':')[0]
        const result = { component: TYPE_COMPONENT_MAP.get(type) }

        // キーを camelCase 化
        for (const [key, value] of Object.entries(item)) {
          result[StringUtil.snakeToCamel(key)] = value
        }

        // プレイス固有の4種は属性と同じ形（値と項目名）に読み替える
        if (VALUE_FIELD_ITEM_TYPES.includes(type)) {
          const value = this.$_getItemValue(type, item)
          result.value = value == null ? null : withPlaceItemUnit(type, value)
          result.attributeName = this.$t(
            `appCardMemberPlace.itemName.${StringUtil.snakeToCamel(type)}`
          )
          result.attributeType = null
        }
        if (type === PLACE_ITEM_TYPE.ATTRIBUTE) {
          result.remSize = this.remSize
        } else if (type === PLACE_ITEM_TYPE.SENSOR_DATA) {
          result.remSize = this.remSize
        } else if (type === PLACE_ITEM_TYPE.SENSOR_NAMES) {
          // names は複数のセンサー名をAPI側でカンマ連結した文字列。紐付けが無い場合は null
          result.value = item.names || null
          result.attributeName = this.$t(
            'appCardMemberPlace.itemName.sensorNames'
          )
          result.attributeType = null
          result.remSize = this.remSize
        } else if (VALUE_FIELD_ITEM_TYPES.includes(type)) {
          result.remSize = this.remSize
        }

        return result
      })
    },
    /**
     * 「他N名」の表示テキスト
     * @type {String}
     */
    otherRelationsText: function () {
      return this.$tc(
        'appCardMemberPlace.others',
        this.otherRelationNames.length
      )
    },
  },
  created: function () {
    this.$_getRemSize()
  },
  methods: {
    /**
     * 1remのサイズ(px)を取得する
     */
    $_getRemSize: function () {
      const fontSize = getComputedStyle(document.documentElement).fontSize
      this.remSize = +fontSize.replace('px', '')
    },
    /**
     * プレイス固有の表示項目の値を求める
     *
     * 値が無い項目は、システムの既定値で動くものだけ既定値を出す（自動チェックイン半径）。
     * 地図ラベルと同じ求め方になるよう `getPlaceItemDefaultValue` を共用している
     * @param {String} type 表示項目の種別
     * @param {Object} item 表示項目の値
     * @returns {Number|String|null} 表示する値
     */
    $_getItemValue: function (type, item) {
      return (
        item[type] ??
        getPlaceItemDefaultValue(type, { indoor_level_id: this.indoorLevelId })
      )
    },
    /**
     * カードをクリックしたときの処理
     */
    $_onClickCard: function () {
      this.$emit('placecard-click', TIMELINE_TYPE, this.id)
    },
    /**
     * プレイス名をクリックしたときの処理
     */
    $_onClickTitle: function () {
      this.$emit('placecard-title-click', TIMELINE_TYPE, this.id)
    },
  },
}
</script>

<style lang="scss" scoped>
// 余白・行間・文字サイズは Figma「プレイスのヘッダー表示について_20260813」に合わせている
// （iField_ユーザー画面 node-id=1205:1362）

// ヘッダーのサブ情報の文字色。ユーザーカード（AppCardUser.vue）の所属グループと同じ
$sub-text-color: #959595;

// ヘッダーの各行の行送り。Figma は 12px の文字を 14px 送りにしている
$header-line-height: 14px;

.card-member-place {
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  row-gap: 12px;
}

.header {
  width: 100%;
  max-width: 100%;
  padding: 0 6px;
  overflow: hidden;
  text-align: left;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  row-gap: 6px;
}

.title-row {
  width: 100%;
  min-width: 0;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  flex-wrap: nowrap;
  column-gap: 8px;
}

.title {
  min-width: 0;
  margin: 0;
  color: $text-color-link;
  font-size: $font-size-middle;
  font-weight: $font-weight-bold;
  line-height: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
}

.attach-file {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  column-gap: 4px;
  color: $sub-text-color;
  font-size: $font-size-middle;
  line-height: $header-line-height;

  &::before {
    content: '';
    display: inline-block;
    width: 14px;
    height: 14px;
    background: url('../../assets/ifield/icon_attach_file.png') no-repeat;
    background-size: 14px 14px;
  }
}

.position-unfixed {
  flex-shrink: 0;
  color: $text-color-error;
  font-size: $font-size-small;
  line-height: $header-line-height;

  // Figma は半角。日本語・英語のどちらでも同じ形にするため CSS 側で付ける
  &::before {
    content: '(';
  }
  &::after {
    content: ')';
  }
}

.field {
  width: 100%;
  min-width: 0;
  color: $sub-text-color;
  font-size: $font-size-small;
  line-height: $header-line-height;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  flex-wrap: nowrap;
}

.field-label {
  flex-shrink: 0;

  &::after {
    content: '：';
  }
}

.field-value {
  min-width: 0;

  &.ellipsis {
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
  }

  &.trailing {
    flex-shrink: 1;
  }

  &.separator {
    flex-shrink: 0;
    margin-right: 4px;
  }

  &.others {
    flex-shrink: 0;
    text-decoration: underline;
    cursor: pointer;
  }
}

.items-container {
  --background-color: #f6f6f6;

  width: 100%;
  overflow: hidden;
  background-color: var(--background-color);
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  grid-auto-rows: max-content;
  justify-items: flex-start;

  // Figma は帯そのものに余白を持たず、項目ひとつずつが余白を持つ。
  // 項目のコンポーネントはユーザーカードと共用なので、こちら側から付ける
  ::v-deep .card-item {
    height: auto;
    padding: 4px 8px;
  }
}
</style>
